import { Injectable, inject } from '@angular/core';
import { Observable, of, map, shareReplay, catchError } from 'rxjs';
import { UserService } from './user-service';
import { UserPreferencesService } from '../user-preferences/user-preferences.service';
import { LocalStorageService } from '../local-storage/localstorage.service';

export interface UserOption {
  id: number | string;
  name: string;
  partnerCompanyId?: number;
  // COL-519: the GetUserList API (SP_UserGet) already returns email/phoneNo/
  // partnerCompanyName on every row — extractUsersFromResponse below just
  // wasn't picking them up. No backend change needed for these.
  email?: string;
  mobileNumber?: string;
  partnerCompanyName?: string;
}

/**
 * Centralized cache for user list data.
 * Fetches the user list API once and shares the result across all components.
 * Use getUserOptions$() for dropdown data and getUserMap$() for ID-to-name lookups.
 */
@Injectable({
  providedIn: 'root',
})
export class UserListCacheService {
  private userService = inject(UserService);
  private userPreferencesService = inject(UserPreferencesService);
  private localStorageService = inject(LocalStorageService);

  private cache$: Observable<UserOption[]> | null = null;
  private mapCache$: Observable<Map<number, string>> | null = null;
  private cacheKey = '';

  /**
   * Get the user options list (for dropdowns).
   * Fetches from API only once per company context; subsequent calls return cached data.
   */
  getUserOptions$(): Observable<UserOption[]> {
    const key = this.buildCacheKey();

    if (this.cache$ && this.cacheKey === key) {
      return this.cache$;
    }

    this.cacheKey = key;
    const prefs = this.userPreferencesService.getUserPreferencesData();
    const partnerCompanyId = this.localStorageService.getItem('partnerCompanyId') || 0;
    const payload = {
      id: 0,
      companyId: prefs?.selectedCompanyId || 0,
      partnerCompanyId,
    };

    this.cache$ = this.userService.getUserList(payload).pipe(
      map(response => this.extractUsersFromResponse(response)),
      shareReplay(1),
    );

    // Reset map cache so it rebuilds from new data
    this.mapCache$ = null;

    return this.cache$;
  }

  /**
   * Get a Map<userId, userName> for ID-to-name lookups.
   * Shares the same underlying API call as getUserOptions$().
   */
  getUserMap$(): Observable<Map<number, string>> {
    if (this.mapCache$ && this.cacheKey === this.buildCacheKey()) {
      return this.mapCache$;
    }

    this.mapCache$ = this.getUserOptions$().pipe(
      map(options => {
        const userMap = new Map<number, string>();
        options.forEach(u => userMap.set(Number(u.id), u.name));
        return userMap;
      }),
      shareReplay(1),
    );

    return this.mapCache$;
  }

  /**
   * Get a Map<userId, UserOption> — same cached options as getUserMap$(),
   * just keyed with the full option (name/email/mobile/partner company)
   * instead of only the name, for callers that need contact details (e.g.
   * ROC's author info popover).
   */
  getUserDetailsMap$(): Observable<Map<number, UserOption>> {
    return this.getUserOptions$().pipe(
      map(options => {
        const detailsMap = new Map<number, UserOption>();
        options.forEach(u => detailsMap.set(Number(u.id), u));
        return detailsMap;
      }),
    );
  }

  private singleUserCache = new Map<number, Observable<UserOption | null>>();

  /**
   * Fallback lookup for a single user by id, unscoped by company —
   * GetUserList/getUserOptions$() only returns users who are members of the
   * currently-selected company (SP_UserGet's list branch joins
   * tblUserCompany on companyId), which structurally excludes Super
   * Admin/Owner accounts. Callers that need to resolve ANY author id (e.g.
   * ROC entries, which can be authored by a Super Admin) should check the
   * bulk map first and fall back to this only on a miss — reuses the
   * existing GET /User/GetUser endpoint (SP_UserGet by id, no company
   * filter), not a new API. Cached per id so repeated misses for the same
   * user don't refetch.
   */
  getUserById$(id: number): Observable<UserOption | null> {
    const cached = this.singleUserCache.get(id);
    if (cached) {
      return cached;
    }
    const obs$ = this.userService.getUser(id).pipe(
      map((response: unknown) => this.mapUserRow(response)),
      catchError(() => of(null)),
      shareReplay(1),
    );
    this.singleUserCache.set(id, obs$);
    return obs$;
  }

  /**
   * Clear the cache (call when company/project context changes).
   */
  clearCache(): void {
    this.cache$ = null;
    this.mapCache$ = null;
    this.cacheKey = '';
    this.singleUserCache.clear();
  }

  /**
   * Build a cache key from the current user context so cache invalidates on context change.
   */
  private buildCacheKey(): string {
    const prefs = this.userPreferencesService.getUserPreferencesData();
    return `${prefs?.userId || 0}_${prefs?.selectedCompanyId || 0}`;
  }

  /**
   * Extract user id/name pairs from API response (handles multiple response shapes).
   */
  private extractUsersFromResponse(response: unknown): UserOption[] {
    const body = response as { data?: unknown[]; items?: unknown[] } | unknown[] | null | undefined;
    const raw = Array.isArray(body) ? body : (body?.data || body?.items || []);
    if (!Array.isArray(raw)) return [];
    return raw.map(u => this.mapUserRow(u)).filter((u): u is UserOption => u !== null && Number(u.id) > 0);
  }

  /** Shared row->UserOption mapping, used for both the bulk list and the single-user fallback. */
  private mapUserRow(row: unknown): UserOption | null {
    if (!row || typeof row !== 'object') {
      return null;
    }
    const u = row as RawUserRow;
    const id = Number(u.id || u.userId || u.Id || u.UserId) || 0;
    return {
      id,
      name: (
        u.userName || u.name || u.Name || u.UserName ||
        ((u.firstName || u.FirstName || '') + ' ' + (u.lastName || u.LastName || '')).trim()
      ) || String(id || ''),
      partnerCompanyId: u.partnerCompanyId !== undefined ? Number(u.partnerCompanyId) : undefined,
      partnerCompanyName: u.partnerCompanyName || u.PartnerCompanyName || undefined,
      email: u.email || u.Email || undefined,
      mobileNumber: u.phoneNo || u.PhoneNo || u.mobileNumber || undefined,
    };
  }
}

/** Raw user row shape as returned by the API — casing is inconsistent across endpoints. */
interface RawUserRow {
  id?: number | string;
  userId?: number | string;
  Id?: number | string;
  UserId?: number | string;
  userName?: string;
  name?: string;
  Name?: string;
  UserName?: string;
  firstName?: string;
  FirstName?: string;
  lastName?: string;
  LastName?: string;
  partnerCompanyId?: number | string;
  partnerCompanyName?: string;
  PartnerCompanyName?: string;
  email?: string;
  Email?: string;
  phoneNo?: string;
  PhoneNo?: string;
  mobileNumber?: string;
}
